import axios, {
    InternalAxiosRequestConfig,
    CancelToken,
    CancelTokenSource
} from "axios";
import qs from "querystring";
import { auth } from "./firebase/FirebaseConfig";
import { isIP } from "./util";

export const resolveBaseUrl = (
    hostname: string,
    envUrl?: string
) => {
    if (envUrl) {
        return envUrl;
    }
    if (
        hostname === "plugweb-dev.herokuapp.com" ||
        hostname === "app.plugco.in" ||
        hostname === "plugweb-test.herokuapp.com" ||
        hostname === "app.theplug.co" ||
        hostname === "web-aws.theplug.co" ||
        hostname === "web.aws.theplug.co"
    ) {
        return "https://api.theplug.co/";
    }
    if (isIP(hostname)) {
        return `http://${hostname}:3000/`;
    }
    return "http://localhost:3000/";
};

axios.defaults.baseURL = resolveBaseUrl(
    window.location.hostname,
    process.env.REACT_APP_BASE_URL
);

axios.defaults.withCredentials = false;
axios.defaults.timeout = 30000;

axios.interceptors.request.use((config) => {
    const existing = config.headers?.get
        ? config.headers.get("Authorization")
        : config.headers?.["Authorization"];
    const token =
        existing ||
        axios.defaults.headers.common?.["Authorization"] ||
        (localStorage.getItem("token")
            ? `bearer ${localStorage.getItem("token")}`
            : undefined);
    if (token && config.headers) {
        if (typeof config.headers.set === "function") {
            config.headers.set("Authorization", token);
        } else {
            config.headers["Authorization"] = token;
        }
    }
    return config;
});

const useFirebaseToken = async (
    config: InternalAxiosRequestConfig
) => {
    const firebaseIdToken = await auth.currentUser?.getIdToken();
    if (firebaseIdToken && config.headers) {
        if (typeof config.headers.set === "function") {
            config.headers.set("FirebaseToken", firebaseIdToken);
        } else {
            config.headers["FirebaseToken"] = firebaseIdToken;
        }
    }
    return config;
};

axios.interceptors.request.use(useFirebaseToken, error => {
    return Promise.reject(error);
});

export const setToken = (manualToken?: string) => {
    const token = manualToken || localStorage.getItem("token");
    if (token) {
        axios.defaults.headers.common["Authorization"] = `bearer ${token}`;
    } else {
        delete axios.defaults.headers.common["Authorization"];
    }
};

setToken();

export const get = <Params>(
    path: string,
    params: Params,
    cancelToken?: CancelToken
) => {
    return axios.get(path, { params, cancelToken });
};

export function getPublic<Params>(path: string, params: Params) {
    const token =
        axios.defaults.headers.common?.["Authorization"] ||
        localStorage.getItem("token");
    return axios.get((!token ? "/public" : "") + path, { params });
}

export function post<Params>(
    path: string,
    params: Params,
    timeout?: number
) {
    return axios.request({
        method: "post",
        url: path,
        data: params,
        timeout
    });
}

export function patch<Params>(path: string, params?: Params) {
    return axios.request({
        method: "patch",
        url: path,
        data: params
    });
}

export function deleteReq<Params>(path: string, params?: Params) {
    return axios.request({
        method: "delete",
        url: path,
        data: params
    });
}

// Legacy

export const getApi = (path: string, params: any) => {
    return axios.get("/api" + path, { params });
};

export const getUser = (path: string, params: any) => {
    return axios.get("/user" + path, { params });
};

export const postQS = (path: string, params: any) => {
    return axios.request({
        method: "post",
        url: path,
        data: qs.stringify(params)
    });
};

export default axios;
